import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Plus, ThumbsUp, Trash2, MessageCircle, Lock, Check, Mail } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  new: '#5B7C99',
  considering: '#E8C265',
  written: '#78A18E',
  declined: '#8C867D',
};
const STATUS_LABEL = {
  new: 'New',
  considering: 'Considering',
  written: 'Written ✓',
  declined: 'Not this one',
};

export default function SanctuaryRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notMember, setNotMember] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [articles, setArticles] = useState([]);
  const [writingReq, setWritingReq] = useState(null); // request being marked as written
  const [selectedArticleId, setSelectedArticleId] = useState('');
  const [writeNote, setWriteNote] = useState('');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (isAdmin) {
      api.get('/articles').then(({ data }) => setArticles(data || [])).catch(() => {});
    }
  }, [isAdmin]);

  const load = async () => {
    try {
      const { data } = await api.get('/sanctuary/requests');
      // sort by upvote count desc
      data.sort((a, b) => b.upvote_count - a.upvote_count);
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 402) setNotMember(true);
      else toast.error('Could not load requests');
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error('Please add a title');
    setSubmitting(true);
    try {
      const { data } = await api.post('/sanctuary/requests', { title: title.trim(), description: description.trim() });
      setItems([data, ...items]);
      setTitle(''); setDescription(''); setShowForm(false);
      toast.success('Your request is in the queue');
    } catch (_) {
      toast.error('Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  const upvote = async (id) => {
    try {
      const { data } = await api.post(`/sanctuary/requests/${id}/upvote`);
      setItems(items.map(x => x.id === id ? { ...x, i_upvoted: data.i_upvoted, upvote_count: data.upvote_count } : x));
    } catch (_) { toast.error('Could not update'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this request?')) return;
    try {
      await api.delete(`/sanctuary/requests/${id}`);
      setItems(items.filter(x => x.id !== id));
    } catch (_) { toast.error('Could not delete'); }
  };

  const updateStatusOnly = async (id, status) => {
    try {
      await api.put(`/admin/sanctuary/requests/${id}/status`, { status });
      setItems(items.map(x => x.id === id ? { ...x, status } : x));
      toast.success('Status updated');
    } catch (_) { toast.error('Could not update'); }
  };

  const openWriteModal = (req) => {
    setWritingReq(req);
    setSelectedArticleId('');
    setWriteNote(req.admin_note || '');
  };

  const confirmWrite = async () => {
    if (!writingReq) return;
    if (!selectedArticleId) { toast.error('Pick an article to link'); return; }
    try {
      const { data } = await api.put(`/admin/sanctuary/requests/${writingReq.id}/status`, {
        status: 'written',
        article_id: selectedArticleId,
        admin_note: writeNote.trim() || undefined,
      });
      setItems(items.map(x => x.id === writingReq.id ? { ...x, status: 'written', admin_note: writeNote.trim() || x.admin_note, article_id: selectedArticleId } : x));
      if (data.email_sent) toast.success('Marked as written & requester notified 📬');
      else toast.success('Marked as written (email skipped — no email on file or Resend off)');
      setWritingReq(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update');
    }
  };

  if (notMember) {
    return (
      <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: '#E8C26525' }}>
            <Lock className="w-8 h-8" style={{ color: '#B38A2E' }} />
          </div>
          <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Sanctuary members only</h1>
          <p className="mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.7 }}>Article requests shape what gets written next. Join The Sanctuary to have your voice heard.</p>
          <button onClick={() => navigate('/sanctuary')} className="px-6 py-3 rounded-full inline-flex items-center gap-2 hover:-translate-y-0.5 transition"
            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 700 }} data-testid="sanctuary-locked-cta">
            Learn about The Sanctuary
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/sanctuary/dashboard" className="inline-flex items-center gap-2 text-sm mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }} data-testid="requests-back">
          <ArrowLeft className="w-4 h-4" /> Back to Sanctuary
        </Link>

        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Article Requests</h1>
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Tell me what to write next. Add a heart to the ones that resonate with you — it's a gentle way to say "me too".</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-5 py-2.5 rounded-full flex items-center gap-2 transition hover:-translate-y-0.5"
            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFFFFF', fontWeight: 700 }}
            data-testid="requests-toggle-form"
          >
            <Plus className="w-4 h-4" />
            <span>{showForm ? 'Cancel' : 'Suggest a topic'}</span>
          </button>
        </div>

        {showForm && (
          <form onSubmit={submit} className="p-6 rounded-3xl mb-8" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0' }} data-testid="requests-form">
            <div className="mb-4">
              <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>What would you like to see?</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={180} required
                placeholder="Topic or question (e.g., How to forgive without forgetting)"
                className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                data-testid="requests-input-title" />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>Any context? <span className="text-xs" style={{ color: '#8C867D', fontWeight: 400 }}>(optional)</span></label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} rows={4}
                placeholder="What made you think of this? What would be helpful to hear?"
                className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2"
                style={{ fontFamily: 'Lora, serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                data-testid="requests-input-description" />
            </div>
            <button type="submit" disabled={submitting} className="px-6 py-3 rounded-full disabled:opacity-50 transition hover:-translate-y-0.5"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFFFFF', fontWeight: 700 }}
              data-testid="requests-submit">
              {submitting ? 'Submitting…' : 'Submit request'}
            </button>
          </form>
        )}

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: '#E57A44' }} /></div>
        ) : items.length === 0 ? (
          <div className="p-10 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', border: '1px dashed #E6E2D8' }} data-testid="requests-empty">
            <MessageCircle className="w-10 h-10 mx-auto mb-3" style={{ color: '#78A18E' }} />
            <p style={{ fontFamily: 'Lora, serif', color: '#595553', fontStyle: 'italic' }}>No requests yet. Be the first to shape what gets written.</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map(it => (
              <li key={it.id} className="p-5 rounded-2xl flex items-start gap-4" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0' }} data-testid={`request-item-${it.id}`}>
                <button onClick={() => upvote(it.id)}
                  className="flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition hover:-translate-y-0.5"
                  style={{ backgroundColor: it.i_upvoted ? '#78A18E' : '#F3EFE7', color: it.i_upvoted ? '#FFFFFF' : '#78A18E', minWidth: '52px' }}
                  data-testid={`request-upvote-${it.id}`}>
                  <ThumbsUp className="w-4 h-4" fill={it.i_upvoted ? '#FFFFFF' : 'none'} />
                  <span className="text-xs font-bold">{it.upvote_count}</span>
                </button>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-base font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{it.title}</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${STATUS_COLORS[it.status] || '#8C867D'}20`, color: STATUS_COLORS[it.status] || '#8C867D', fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>{STATUS_LABEL[it.status] || it.status}</span>
                  </div>
                  {it.description && <p className="text-sm mb-2" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.7 }}>{it.description}</p>}
                  <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{it.user_name || 'A member'} · {new Date(it.created_at).toLocaleDateString()}</p>
                  {it.admin_note && (
                    <div className="mt-2 p-3 rounded-lg text-xs" style={{ backgroundColor: '#FFF5E5', border: '1px solid #E8C26540', fontFamily: 'Nunito, sans-serif', color: '#4A3D1E' }}>
                      <strong>From Genesis:</strong> {it.admin_note}
                    </div>
                  )}
                  {isAdmin && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid={`admin-actions-${it.id}`}>
                      <select
                        value={it.status}
                        onChange={(e) => {
                          const next = e.target.value;
                          if (next === 'written') openWriteModal(it);
                          else updateStatusOnly(it.id, next);
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                        style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#F3EFE7', color: '#2C2724', border: '1px solid #E6E2D8' }}
                        data-testid={`admin-status-select-${it.id}`}
                      >
                        <option value="new">New</option>
                        <option value="considering">Considering</option>
                        <option value="written">Written ✓</option>
                        <option value="declined">Not this one</option>
                      </select>
                      {it.status !== 'written' && (
                        <button
                          onClick={() => openWriteModal(it)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition hover:-translate-y-0.5"
                          style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFFFFF' }}
                          data-testid={`admin-mark-written-${it.id}`}
                        >
                          <Mail className="w-3 h-3" /> Mark written & notify
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <button onClick={() => remove(it.id)} title="Delete" className="opacity-40 hover:opacity-100 transition" data-testid={`request-delete-${it.id}`}>
                  <Trash2 className="w-4 h-4" style={{ color: '#8C867D' }} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {writingReq && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(44, 39, 36, 0.6)' }} data-testid="mark-written-modal">
          <div className="w-full max-w-lg p-6 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 30px 80px rgba(0,0,0,0.25)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#78A18E20', color: '#78A18E' }}>
                <Check className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Mark as written</h3>
                <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Link the article you wrote for this request. The requester will be emailed.</p>
              </div>
            </div>
            <p className="text-sm mb-3" style={{ fontFamily: 'Lora, serif', color: '#595553', fontStyle: 'italic' }}>"{writingReq.title}"</p>
            <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>Choose the article</label>
            <select
              value={selectedArticleId}
              onChange={(e) => setSelectedArticleId(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border mb-4"
              style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
              data-testid="mark-written-article-select"
            >
              <option value="">— Pick a published article —</option>
              {articles.filter(a => a.status === 'published').map(a => (
                <option key={a.id} value={a.id}>{a.title}</option>
              ))}
            </select>
            <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>Personal note <span className="text-xs" style={{ color: '#8C867D', fontWeight: 400 }}>(shown in the email)</span></label>
            <textarea
              value={writeNote}
              onChange={(e) => setWriteNote(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="Optional — a warm line to the requester"
              className="w-full px-4 py-2.5 rounded-xl border mb-5"
              style={{ fontFamily: 'Lora, serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
              data-testid="mark-written-note"
            />
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setWritingReq(null)}
                className="px-4 py-2 rounded-full text-sm"
                style={{ fontFamily: 'Manrope, sans-serif', color: '#595553', fontWeight: 600 }}
                data-testid="mark-written-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmWrite}
                className="px-5 py-2 rounded-full text-sm flex items-center gap-2 transition hover:-translate-y-0.5"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFFFFF', fontWeight: 700 }}
                data-testid="mark-written-confirm"
              >
                <Mail className="w-4 h-4" /> Notify & mark written
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
